import { useState } from 'react';

import {
  validateSettings,
  type SettingsFieldErrors,
  type SettingsFormValues,
} from '../features/settings/application/settingsForm';
import { Alert, Button, Card, DateField, TextField } from '../shared/components';

import styles from './ScreenLayout.module.css';
import settingsStyles from './SettingsPage.module.css';

/**
 * The phases the fund runs in, for the committee manager.
 *
 * Everything here is read by the employee screens at runtime: the declaration
 * phase's dates decide whether that form is open, and the proof requirements
 * decide which documents appear on the checklist. Nothing on this screen is
 * decorative.
 */

export interface RelationSetting {
  readonly relation: string;
  readonly labelAr: string;
}

export interface SettingsPageProps {
  readonly relations: readonly RelationSetting[];
  readonly initialValues: SettingsFormValues;
  readonly onSave: (values: SettingsFormValues) => Promise<void>;
  readonly errorAr: string | null;
  readonly savedAr: string | null;
}

export function SettingsPage({
  relations,
  initialValues,
  onSave,
  errorAr,
  savedAr,
}: SettingsPageProps) {
  const [values, setValues] = useState<SettingsFormValues>(initialValues);
  const [errors, setErrors] = useState<SettingsFieldErrors>({});
  const [saving, setSaving] = useState(false);

  const set =
    (field: keyof SettingsFormValues) => (event: { target: { value: string } }) => {
      setValues((current) => ({ ...current, [field]: event.target.value }));
    };

  const toggleRelation =
    (field: 'allowed' | 'proofRequired', relation: string) => (checked: boolean) => {
      setValues((current) => ({
        ...current,
        [field]: { ...current[field], [relation]: checked },
      }));
    };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const result = validateSettings(values);
    setErrors(result.errors);
    if (!result.valid) return;

    setSaving(true);
    void onSave(values).finally(() => {
      setSaving(false);
    });
  };

  return (
    <div className={styles.page}>
      <h1 className="visually-hidden">إعدادات المراحل</h1>

      <form onSubmit={handleSubmit} noValidate>
        {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}
        {savedAr ? <Alert severity="success">{savedAr}</Alert> : null}

        <Card
          title="مرحلة الإقرار الطبي"
          description="خارج هذه الفترة تُعرض شاشة الإقرار للقراءة فقط"
        >
          <div className={styles.formGrid}>
            <DateField
              label="تفتح في"
              required
              value={values.opensOn}
              onChange={(opensOn) => {
                setValues((current) => ({ ...current, opensOn }));
              }}
              error={errors.opensOn}
            />
            <DateField
              label="تغلق في"
              required
              value={values.closesOn}
              onChange={(closesOn) => {
                setValues((current) => ({ ...current, closesOn }));
              }}
              error={errors.closesOn}
              hint="يشمل اليوم كاملًا"
            />
          </div>

          {/* §11.5.2 — the stop that does not need a deploy. Worded as what it
              does, not as a flag name. */}
          <label className={settingsStyles.switchRow}>
            <input
              type="checkbox"
              checked={values.submissionsEnabled}
              onChange={(event) => {
                setValues((current) => ({
                  ...current,
                  submissionsEnabled: event.target.checked,
                }));
              }}
            />
            <span>
              السماح للموظفين بإدخال بياناتهم ورفع وثائقهم
              <small className={settingsStyles.switchHint}>
                إيقافه يمنع كل إدخال فورًا، دون انتظار انتهاء الفترة
              </small>
            </span>
          </label>
        </Card>

        <Card
          title="مرحلة إدخال البيانات والوثائق"
          description="من يشمله الصندوق، وما الوثيقة المطلوبة لكل صلة"
        >
          <div className={styles.formGrid}>
            <TextField
              label="الحد الأقصى لعدد المعالين"
              required
              numeric
              inputMode="numeric"
              value={values.maxDependents}
              onChange={set('maxDependents')}
              error={errors.maxDependents}
            />
            <TextField
              label="سن الابن/الابنة"
              numeric
              inputMode="numeric"
              value={values.childMaxAge}
              onChange={set('childMaxAge')}
              error={errors.childMaxAge}
              hint="يُترك فارغًا إن لم يكن هناك حد"
            />
          </div>

          <table className={settingsStyles.table}>
            <thead>
              <tr>
                <th scope="col">صلة القرابة</th>
                <th scope="col">مشمولة</th>
                <th scope="col">تتطلب وثيقة</th>
              </tr>
            </thead>
            <tbody>
              {relations.map((relation) => (
                <tr key={relation.relation}>
                  <th scope="row">{relation.labelAr}</th>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${relation.labelAr}: مشمولة`}
                      checked={values.allowed[relation.relation] ?? false}
                      onChange={(event) => {
                        toggleRelation('allowed', relation.relation)(event.target.checked);
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${relation.labelAr}: تتطلب وثيقة`}
                      checked={values.proofRequired[relation.relation] ?? false}
                      onChange={(event) => {
                        toggleRelation(
                          'proofRequired',
                          relation.relation,
                        )(event.target.checked);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="التغطية" description="بالريال اليمني، سنويًا لكل شخص مشمول">
          <div className={styles.formGrid}>
            <TextField
              label="تكلفة الاشتراك للفرد"
              required
              numeric
              inputMode="numeric"
              value={values.costPerPerson}
              onChange={set('costPerPerson')}
              error={errors.costPerPerson}
            />
            <TextField
              label="سقف ما يدفعه الصندوق للفرد"
              required
              numeric
              inputMode="numeric"
              value={values.ceilingPerPerson}
              onChange={set('ceilingPerPerson')}
              error={errors.ceilingPerPerson}
            />
          </div>
        </Card>

        <div className={settingsStyles.actions}>
          <Button type="submit" loading={saving}>
            حفظ الإعدادات
          </Button>
        </div>
      </form>
    </div>
  );
}
